import { TimeRule, ZmanimData, RoundingConfig } from '../types';
import { getIsraelTimeHHMM } from './israelTime';
import { CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET } from '../config/constants';

/** Add or subtract minutes from a "HH:mm" string. Returns "HH:mm". */
export function applyOffset(baseTime: string, offsetMinutes: number): string {
  const [hours, minutes] = baseTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + offsetMinutes;
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Round a "HH:mm" string to nearest N minutes in given direction. */
export function applyRounding(time: string, config: RoundingConfig): string {
  const [hours, minutes] = time.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes;
  const { to, direction } = config;
  let rounded: number;
  if (direction === 'down') {
    rounded = Math.floor(totalMinutes / to) * to;
  } else if (direction === 'up') {
    rounded = Math.ceil(totalMinutes / to) * to;
  } else {
    rounded = Math.round(totalMinutes / to) * to;
  }
  const normalized = ((rounded % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Resolve a TimeRule to a "HH:mm" string using today's zmanim. */
/** Zman key for candle lighting (Yoel, October 4, 2026): minyan times and opening hours can link to it. */
export const CANDLE_LIGHTING_ZMAN = 'candleLighting';

/** The day's candle lighting; on days without one, sunset minus the usual minutes. */
export function candleLightingTime(zmanim: ZmanimData): string {
  if (zmanim.candleLighting) return zmanim.candleLighting;
  return zmanim.SunsetDefault ? applyOffset(zmanim.SunsetDefault, -CANDLE_LIGHTING_MINUTES_BEFORE_SUNSET) : '';
}

/** The "not later than" limit for today, or undefined when the rule has none (or the zman is missing). */
export function notLaterThanTime(rule: TimeRule, zmanim: ZmanimData): string | undefined {
  if (rule.type !== 'fixed' || !rule.notLaterThan) return undefined;
  const { baseZman, offsetMinutes, rounding } = rule.notLaterThan;
  const limit = resolveTimeRule({ type: 'zmanOffset', baseZman, offsetMinutes, ...(rounding ? { rounding } : {}) }, zmanim);
  return limit === '00:00' ? undefined : limit;
}

export function resolveTimeRule(rule: TimeRule, zmanim: ZmanimData): string {
  if (rule.type === 'fixed') {
    const limit = notLaterThanTime(rule, zmanim);
    return limit && limit < rule.time ? limit : rule.time;
  }
  const baseTime =
    rule.baseZman === CANDLE_LIGHTING_ZMAN
      ? candleLightingTime(zmanim)
      : (zmanim[rule.baseZman as keyof ZmanimData] as string);
  if (!baseTime) return '00:00';
  const withOffset = applyOffset(baseTime, rule.offsetMinutes);
  if (rule.rounding) {
    return applyRounding(withOffset, rule.rounding);
  }
  return withOffset;
}

/** Current Israel time as "HH:mm" (not the phone's own time zone). */
export function getCurrentTimeHHMM(): string {
  return getIsraelTimeHHMM();
}

/** Returns true if the given "HH:mm" time is in the past today */
export function isTimePassed(time: string): boolean {
  return time < getCurrentTimeHHMM();
}

/** Compare two "HH:mm" strings. Returns negative if a < b. */
export function compareHHMM(a: string, b: string): number {
  return a.localeCompare(b);
}
